import { markRaw, ref, readonly, shallowReadonly, computed, watch } from "vue";
import { useLanesManager } from "@/stores/lanes-store";
import { useProcessElementsManager } from "@/stores/process-elements-store.js";
import { useProcessManager } from "@/composables/process-manager";
import type { BaseViewerOptions } from "bpmn-js/lib/BaseViewer";
import type { ImportBPMNDiagramError as OriginalImportDiagramError } from "bpmn-js/lib/import/Importer";
import { is, getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import Modeler from "bpmn-js/lib/Modeler";
import Viewer from "bpmn-js/lib/Viewer";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import ModelingModule from "bpmn-js/lib/features/modeling";
import lintConfig from "./.bpmnlintrc.js";
import type { Element as BPMNElement } from "bpmn-js/lib/model/Types";
import type { ElementLike } from "diagram-js/lib/model/Types";
import type { CanvasViewbox } from "diagram-js/lib/core/Canvas";
import type ContextPad from "diagram-js/lib/features/context-pad/ContextPad";
import { isPaste } from "diagram-js/lib/features/keyboard/KeyboardUtil";
import {
    Modeler as BaseModeler,
    HIGH_PRIORITY,
    elementIsSubProcess,
    createReviver,
    isProcessParticipant,
    getMainParticipant,
    elementIsLane,
    elementIsTask,
    elementIsEvent,
    elementIsGateway,
    elementIsMessageFlow,
    elementIsSequenceFlow,
} from "./helpers";
import CustomRendererModule from "./custom-modules";
import StekzModdle from "./stekz-moddle.json";
// @ts-expect-error: Package doesn't have types.
import BpmnColorPickerModule from "bpmn-js-color-picker/colors";
import {
    BpmnPropertiesPanelModule,
    BpmnPropertiesProviderModule,
    // @ts-expect-error: Package doesn't have types.
} from "bpmn-js-properties-panel";
// @ts-expect-error: Package doesn't have types.
import LintModule from "bpmn-js-bpmnlint";
// @ts-expect-error: Package doesn't have types.
import GridModule from "diagram-js-grid";
// @ts-expect-error: Package doesn't have types.
import TokenSimulationModule from "bpmn-js-token-simulation";

/**
 * Overwrite wrong types for Canvas.zoom.
 */
declare module "diagram-js/lib/core/Canvas" {
    export default interface Canvas {
        zoom(newScale?: number | "fit-viewport", center?: Point | true): number;
    }
}

/**
 * Warnings are wrongly typed as `string`, but actually look like this.
 * Added the `string` as a type as well since we MAY be wrong in some cases?
 */
export type ImportWarning = {
    message: string;
    [key: string]: unknown;
} & string;
export type ImportBPMNDiagramError = OriginalImportDiagramError & {
    warnings: ImportWarning[];
};

export function useBpmnManager<BpmnJs extends BaseModeler>() {
    const bpmnJs = ref<BpmnJs>();
    const isChanged = ref(false);
    const bpmnJsElement = ref<HTMLElement>();
    const selectedBpmnElement = ref<BPMNElement>();
    const hoveredBpmnElement = ref<BPMNElement>();
    const xml = ref("");
    const scrollingEnabled = ref(false);
    const navigationEnabled = ref(false);
    const zoomOnHoverEnabled = ref(false);
    const xOffsetWhenCentered = ref(0);
    const yOffsetWhenCentered = ref(0);
    const { lanes } = useLanesManager();
    const { processElements } = useProcessElementsManager();
    const { process } = useProcessManager();

    const eventBus = computed(() => bpmnJs.value?.get("eventBus"));

    const detachBeforeInit = () => {
        if (bpmnJsElement.value) {
            bpmnJsElement.value.removeEventListener("wheel", wheelZoomListener);
            bpmnJsElement.value.removeEventListener(
                "wheel",
                wheelScrollListener
            );
        }

        if (bpmnJs.value) {
            bpmnJs.value.clear();
            bpmnJs.value.detach();
            try {
                bpmnJs.value.get("propertiesPanel")?.detach();
            } catch {
                // Has no propertiesPanel
            }
        }
        xml.value = "";

        selectedBpmnElement.value = undefined;
        hoveredBpmnElement.value = undefined;
    };

    /**
     * Manually trigger a mousedown event on the root element to complete active actions.
     * This is useful to complete ongoing action, for example when
     * the user is editing a label.
     */
    const completeActiveActions = async () => {
        const rootElement = getRootElement();

        return new Promise<void>((resolve, reject) => {
            const callback = ({ element }: { element: BPMNElement }) => {
                if (!rootElement) reject();

                if (element.id == rootElement?.id) {
                    resolve();
                }
                // Remove the listener
                bpmnJs.value?.off("element.mousedown", callback);
            };

            bpmnJs.value?.on<{ element: BPMNElement } & Event>(
                "element.mousedown",
                callback
            );

            eventBus.value?.fire("element.mousedown", {
                element: rootElement,
                originalEvent: { target: rootElement },
            });
        });
    };

    /**
     * Initialize as a BPMN Modeler.
     */
    const initModeler = (
        element: HTMLElement,
        propertiesPanelElement?: HTMLElement,
        {
            hideLaneNames = false,
        }: {
            hideLaneNames?: boolean;
        } = {}
    ) => {
        isChanged.value = false;
        detachBeforeInit();
        const additionalModules: BaseViewerOptions["additionalModules"] = [
            ModelingModule,
            TokenSimulationModule,
            LintModule,
            BpmnColorPickerModule,
            GridModule,
        ];

        if (hideLaneNames) {
            additionalModules.push(CustomRendererModule);
        }

        if (propertiesPanelElement) {
            additionalModules.push(
                BpmnPropertiesPanelModule,
                BpmnPropertiesProviderModule
            );
        }
        bpmnJs.value = markRaw(
            new Modeler({
                additionalModules,
                moddleExtensions: {
                    stekz: StekzModdle,
                },
                linting: {
                    bpmnlint: lintConfig,
                    active: false,
                },
                zoomScroll: {
                    scale: 0.3,
                    enabled: false,
                },
            }) as BpmnJs
        );
        scrollingEnabled.value = true;
        navigationEnabled.value = true;
        attachTo(element);
        if (propertiesPanelElement) {
            attachPropertiesPanel(propertiesPanelElement);
        }
        addBpmnEventListeners();
    };

    /**
     * Initialize as a BPMN Viewer.
     */
    const initViewer = (
        element?: HTMLElement | undefined,
        {
            isNavigationEnabled = true,
            isScrollingEnabled = true,
            isZoomOnHoverEnabled = false,
            hideLaneNames = false,
        }: {
            isNavigationEnabled?: boolean;
            isScrollingEnabled?: boolean;
            isZoomOnHoverEnabled?: boolean;
            hideLaneNames?: boolean;
        } = {}
    ) => {
        detachBeforeInit();
        const _Viewer = isNavigationEnabled ? NavigatedViewer : Viewer;
        const additionalModules: BaseViewerOptions["additionalModules"] = [
            ModelingModule,
        ];
        if (hideLaneNames) {
            additionalModules.push(CustomRendererModule);
        }
        if (isNavigationEnabled) {
            additionalModules.push(GridModule);
        }
        bpmnJs.value = markRaw(
            new _Viewer({
                additionalModules: additionalModules,
                moddleExtensions: {
                    stekz: StekzModdle,
                },
                zoomScroll: {
                    scale: 0.3,
                    enabled: false,
                },
            }) as BpmnJs
        );

        scrollingEnabled.value = isScrollingEnabled;
        navigationEnabled.value = isNavigationEnabled;
        zoomOnHoverEnabled.value = isZoomOnHoverEnabled;
        if (element) {
            attachTo(element);
        }
        addBpmnEventListeners();
    };

    const getRootElement = () => {
        const canvas = bpmnJs.value?.get("canvas");
        return canvas?.getRootElement();
    };

    /**
     * Set the root element.
     */
    const setRootElement = (id: string) => {
        const canvas = bpmnJs.value?.get("canvas");
        const rootElement = canvas?.findRoot(id);
        if (rootElement) {
            canvas?.setRootElement(rootElement);
        }
    };

    /**
     * Injects styles into the BPMN container.
     */
    const injectStyles = (newStyles: string) => {
        if (!bpmnJsElement.value) {
            return;
        }
        const styleNode =
            bpmnJsElement.value.querySelector("style") ||
            document.createElement("style");
        styleNode.innerHTML = newStyles;
        bpmnJsElement.value.insertBefore(
            styleNode,
            bpmnJsElement.value.firstChild
        );
    };

    /**
     * Listens to "wheel" event to zoom in and out.
     */
    const wheelZoomListener = (event: WheelEvent) => {
        if (!bpmnJs.value || !navigationEnabled.value) {
            return;
        }

        if (!event.metaKey && !event.ctrlKey) {
            // Only act if CMD or CTRL is clicked
            return;
        }
        event.preventDefault();

        const elementRect = bpmnJs.value
            .get("canvas")
            .getContainer()
            .getBoundingClientRect();
        const offset = {
            x: event.clientX - elementRect.left,
            y: event.clientY - elementRect.top,
        };

        const factor = -0.75 * (event.deltaMode === 0 ? 0.02 : 0.32);
        const delta =
            Math.sqrt(Math.pow(event.deltaY, 2) + Math.pow(event.deltaX, 2)) *
            Math.sign(event.deltaY) *
            factor;

        bpmnJs.value.get("zoomScroll")!.zoom(delta, offset);
    };

    /**
     * Listens to "wheel" event to scroll
     */
    const wheelScrollListener = (event: WheelEvent) => {
        if (
            !bpmnJs.value ||
            !scrollingEnabled.value ||
            !navigationEnabled.value
        ) {
            return;
        }

        if (event.metaKey || event.ctrlKey) {
            // Only act if CMD or CTRL is NOT clicked
            return;
        }

        // If the contextPad is open the wheel shouldn't be intercepted
        if (
            bpmnJs.value instanceof Modeler &&
            bpmnJs.value.get<ContextPad>("contextPad")?.isShown()
        ) {
            return;
        }

        event.preventDefault();

        const factor = -0.75 * (event.deltaMode === 0 ? 1.0 : 16.0);
        const delta = { dx: 0, dy: 0 };

        if (event.shiftKey) {
            // Force horizontal scrolling
            delta.dx = factor * event.deltaY;
        } else {
            delta.dx = factor * event.deltaX;
            delta.dy = factor * event.deltaY;
        }
        bpmnJs.value.get("zoomScroll")?.scroll(delta);
    };

    const stepZoom = (stepSize: number) => {
        bpmnJs.value?.get("zoomScroll")?.stepZoom(stepSize);
    };

    const resetZoom = () => {
        const canvas = bpmnJs.value?.get("canvas");
        // Avoid resetting the zoom if the container is gone. This prevents
        // errors being thrown when resetZoom is being called right before
        // the component is unmounted.
        if (!canvas?.getContainer()) {
            return;
        }

        canvas.zoom("fit-viewport", true);

        const viewbox = canvas.viewbox();
        xOffsetWhenCentered.value = viewbox.x;
        yOffsetWhenCentered.value = viewbox.y;
    };

    /**
     * Attach BpmnJs instance to DOM.
     */
    const attachTo = (element: HTMLElement) => {
        bpmnJs.value?.attachTo(element);

        bpmnJsElement.value = element;
        // Zoom and scroll with scrollwheel
        bpmnJsElement.value.addEventListener("wheel", wheelZoomListener, {
            passive: false,
        });
        bpmnJsElement.value.addEventListener("wheel", wheelScrollListener, {
            passive: false,
        });
    };

    /**
     * Attach the propertiesPanel.
     */
    const attachPropertiesPanel = (element: HTMLElement) => {
        const propertiesPanel = bpmnJs.value?.get("propertiesPanel");
        propertiesPanel?.attachTo(element);
    };

    /**
     * Load the BPMN/XML into the canvas
     */
    const loadBpmn = async (
        xml_: string
    ): Promise<{ warnings?: ImportWarning[] }> => {
        if (xml_ === xml.value || !bpmnJs.value) {
            return {};
        }

        if (!xml_) {
            bpmnJs.value.clear();
            xml.value = xml_;
            return {};
        }

        try {
            const { warnings } = await bpmnJs.value.importXML(xml_);
            xml.value = xml_;
            // E.g. 'unknown attribute'.
            if (warnings.length) {
                console.warn(warnings);
                return { warnings } as unknown as { warnings: ImportWarning[] };
            }
            // Zoom and center in-view
            resetZoom();
            updateProcessElementProperties();
            updateLaneProperties();
        } catch (e) {
            detachBeforeInit();
            throw e as ImportBPMNDiagramError;
        }

        return {};
    };

    const getSvg = async () => {
        if (!bpmnJs.value) {
            return;
        }
        const { svg } = await bpmnJs.value.saveSVG();
        return svg;
    };

    const resize = () => {
        const canvas = bpmnJs.value?.get("canvas");
        canvas?.resized();
    };

    const getViewbox = (): CanvasViewbox | undefined => {
        return bpmnJs.value?.get("canvas")?.viewbox();
    };

    const setViewbox = (viewBox: CanvasViewbox) => {
        bpmnJs.value?.get("canvas")?.viewbox(viewBox);
    };

    /**
     * Add event listeners to the bpmn-js instance.
     */
    const addBpmnEventListeners = () => {
        if (!bpmnJs.value || !bpmnJsElement.value) {
            return;
        }
        // Set the selectedBpmnElement.
        bpmnJs.value!.on<{ element: BPMNElement }>(
            "element.click",
            ({ element }) => {
                if (element === selectedBpmnElement.value) {
                    selectedBpmnElement.value = undefined;
                } else {
                    selectedBpmnElement.value = markRaw(element);
                }
            }
        );

        const canvas = bpmnJs.value!.get("canvas");

        const targetViewboxWidth = 700;
        if (zoomOnHoverEnabled.value) {
            const onMouseMove = (event: {
                element: BPMNElement;
                originalEvent: MouseEvent;
            }) => {
                const viewbox = canvas.viewbox();
                const outerBox = canvas.getSize();
                const widthScale = outerBox.width / viewbox.inner.width;
                const heightScale = outerBox.height / viewbox.inner.height;
                // The scale also used internally by bpmn-js to scale the
                // diagram for the new viewbox.
                const scale = Math.min(widthScale, heightScale);

                // No need to show a zoomed in viewbox when it isn't zoomed
                // far enough in the first place.
                if (scale > 0.65) {
                    return;
                }

                const originalEvent = event.originalEvent;
                const clientWidth = bpmnJsElement.value!.clientWidth;
                const clientHeight = bpmnJsElement.value!.clientHeight;

                // The x / y coordinates in percentages.
                const xPercentage = originalEvent.layerX / clientWidth;
                const yPercentage = originalEvent.layerY / clientHeight;
                const scaleByWidth = widthScale > heightScale;

                // The factor we need to multiply the inner height with to
                // maintain the aspect ratio while targeting a specific width.
                const width = targetViewboxWidth;
                const height = viewbox.inner.height * scale;

                // When the mouse is on a specific section of the diagram,
                // we want to center the viewbox on that section, with the
                // mouse in exactly the middle of that viewbox. Here we
                // calculate the offsets so that we can calculate the top-left
                // coordinates of the viewbox.
                const xViewboxOffset = width / 2;
                const yViewboxOffset = height / 2;

                // The x / y coordinates of the position in the diagram using
                // the relative position of the mouse.
                const x = viewbox.inner.width * xPercentage;
                const y = viewbox.inner.height * yPercentage;

                // Only one of the offsets should be scaled to the new scale,
                // the other one will follow. Don't ask me why.
                const xOffset =
                    xOffsetWhenCentered.value * (scaleByWidth ? scale : 1);
                const yOffset =
                    yOffsetWhenCentered.value * (scaleByWidth ? 1 : scale);

                canvas.viewbox({
                    x: xOffset + x - xViewboxOffset,
                    y: yOffset + y - yViewboxOffset,
                    width: width,
                    height: height,
                });
            };

            bpmnJs.value.on("element.mousemove", onMouseMove);

            // When mouse leaves the bpmn js element, reset viewbox.
            bpmnJsElement.value.addEventListener("mouseleave", () => {
                resetZoom();
            });
        }

        // Set the hoveredBpmnElement.
        bpmnJs.value.on<{ element: BPMNElement }>(
            "element.hover",
            ({ element }) => {
                hoveredBpmnElement.value = markRaw(element);
            }
        );
        bpmnJs.value.on<{ element: BPMNElement }>("element.out", () => {
            hoveredBpmnElement.value = undefined;
        });

        // Update the XML.
        bpmnJs.value.on("commandStack.changed", async () => {
            isChanged.value = true;
            const result = await bpmnJs.value?.saveXML({ format: true });
            xml.value = result?.xml || "";
        });

        bpmnJs.value.on<Event & { element: ElementLike }>(
            "element.dblclick",
            HIGH_PRIORITY,
            function ({ element }) {
                if (
                    is(element, "bpmn:Lane") ||
                    (is(element, "bpmn:Participant") &&
                        isProcessParticipant(element, process.value!.identity))
                ) {
                    // Cancel edit for lane and main participant labels
                    return false;
                }
            }
        );

        // Copy
        bpmnJs.value.on<{ tree: unknown }>(
            "copyPaste.elementsCopied",
            ({ tree }) => {
                localStorage.setItem("bpmnClipboard", JSON.stringify(tree));
            }
        );

        // Paste
        const moddle = bpmnJs.value.get("moddle");
        try {
            const clipboard = bpmnJs.value.get("clipboard");
            if (!clipboard) return;
            bpmnJs.value!.get("keyboard")?.addListener(2000, (event) => {
                const { keyEvent } = event;

                if (!isPaste(keyEvent)) {
                    return;
                }

                const serializedCopy = localStorage.getItem("bpmnClipboard");

                if (!serializedCopy) {
                    return;
                }

                const parsedCopy = JSON.parse(
                    serializedCopy,
                    createReviver(moddle)
                );

                clipboard.set(parsedCopy);
            });
        } catch {
            // Clipbord does not exists.
        }
    };

    /**
     * Get all subprocesses from the BPMN.
     */
    const getSubProcesses = (): BPMNElement[] => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.filter(elementIsSubProcess) as BPMNElement[];
    };

    /**
     * Get all subprocesses from the BPMN.
     */
    const getLanes = async (): Promise<BPMNElement[]> => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.filter(elementIsLane) as BPMNElement[];
    };

    /**
     * Get the subprocess for a given activity id.
     * This is NOT the element.id for a subprocess, but the element.businessObject.id.
     */
    const getSubProcessById = (businessObjectId: string) => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.find((element: ElementLike) => {
            return (
                elementIsSubProcess(element as BPMNElement) &&
                element.businessObject.id === businessObjectId
            );
        }) as BPMNElement;
    };

    /**
     * Open a subprocess inside the editor.
     */
    const selectSubProcess = (element: BPMNElement) => {
        if (!elementIsSubProcess(element)) {
            element = getSubProcessById(element.businessObject.id);
        }
        const canvas = bpmnJs.value?.get("canvas");
        canvas?.setRootElement(element);
    };

    /**
     * Destroy the bpmn-js component.
     */
    const destroy = () => {
        bpmnJs.value?.destroy();
    };

    const updateProcessElementProperties = () => {
        if (!bpmnJs?.value || !processElements?.value) return;

        const modeling = bpmnJs.value.get("modeling");
        const elementRegistry = bpmnJs.value.get("elementRegistry");
        let somethingHasChanged = false;

        elementRegistry
            .filter(
                (element: BPMNElement) =>
                    elementIsTask(element) ||
                    elementIsEvent(element) ||
                    elementIsGateway(element) ||
                    elementIsMessageFlow(element) ||
                    elementIsSequenceFlow(element)
            )
            .forEach((element: BPMNElement) => {
                const businessObject = getBusinessObject(element);
                const processElement = processElements?.value?.find(
                    (t) => t.bpmn_id === businessObject.id
                );
                if (!processElement) return;

                if (processElement.identity === businessObject.processElementId)
                    return;

                somethingHasChanged = true;
                modeling.updateProperties(element as BPMNElement, {
                    processElementId: processElement.identity,
                });
            });
        if (somethingHasChanged) {
            // Trigger a change for bpmnJs as well.
            eventBus.value?.fire("commandStack.changed");
        }
    };

    const updateLaneProperties = () => {
        if (!bpmnJs.value || !lanes.value) return;

        const modeling = bpmnJs.value.get("modeling");
        const elementRegistry = bpmnJs.value.get("elementRegistry");
        let somethingHasChanged = false;

        elementRegistry
            .filter((element) => is(element, "bpmn:Lane"))
            .forEach((laneElement) => {
                const businessObject = getBusinessObject(laneElement);
                const lane = lanes?.value?.find(
                    (l) => l.bpmn_id === businessObject.id
                );
                if (!lane) return;

                if (
                    lane.name === businessObject.name &&
                    lane.role_id === businessObject.roleId
                )
                    return;

                somethingHasChanged = true;
                modeling.updateProperties(laneElement as BPMNElement, {
                    name: lane.name,
                    roleId: lane.role_id,
                });
            });
        if (somethingHasChanged) {
            // Trigger a change for bpmnJs as well.
            eventBus.value?.fire("commandStack.changed");
        }
    };

    watch(processElements, async () => {
        updateProcessElementProperties();
    });

    watch(lanes, async () => {
        updateLaneProperties();
    });

    const updateElementProperties = (
        id: string,
        newProperties: Record<string, unknown>
    ) => {
        const element = bpmnJs.value
            ?.get("elementRegistry")
            ?.find((el) => getBusinessObject(el).id === id);
        if (!element) {
            throw Error("Cannot find provided element by ID.");
        }
        const modeling = bpmnJs.value?.get("modeling");
        modeling?.updateProperties(element as BPMNElement, newProperties);
        eventBus.value?.fire("commandStack.changed");
    };

    watch([bpmnJs, xml, process], async ([newBpmnJs, newXml, newProcess]) => {
        if (!newBpmnJs || !newXml || !newProcess) return;

        const participant = await getMainParticipant(
            newBpmnJs,
            newProcess.identity
        );
        if (!participant) return;

        const businessObject = getBusinessObject(participant);
        if (
            businessObject.name === newProcess.name &&
            businessObject.processId === newProcess.identity
        ) {
            return;
        }

        // Update the name and set as main explicitly
        const modeling = newBpmnJs.get("modeling");
        modeling.updateProperties(participant as BPMNElement, {
            name: newProcess.name,
            processId: newProcess.identity,
        });

        // Trigger a change for bpmnJs as well.
        eventBus.value?.fire("commandStack.changed");
    });

    return {
        bpmnJs: shallowReadonly(bpmnJs),
        xml: readonly(xml),
        get initialized() {
            return computed(() => !!bpmnJs.value);
        },
        scrollingEnabled,
        resize,
        stepZoom,
        resetZoom,
        getSvg,
        getViewbox,
        setViewbox,
        initModeler,
        initViewer,
        injectStyles,
        attachTo,
        attachPropertiesPanel,
        loadBpmn,
        hoveredBpmnElement,
        selectedBpmnElement,
        destroy,
        isChanged: readonly(isChanged),
        getSubProcesses,
        getSubProcessById,
        selectSubProcess,
        getLanes,
        completeActiveActions,
        getRootElement,
        setRootElement,
        get elementRegistry() {
            return bpmnJs.value?.get("elementRegistry");
        },
        get canvas() {
            return bpmnJs.value?.get("canvas");
        },
        get selection() {
            return bpmnJs.value?.get("selection");
        },
        get eventBus() {
            return eventBus.value;
        },
        updateElementProperties,
    };
}
